import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
  Search, ArrowRight, Sparkles,
  SlidersHorizontal, UserCheck, Star, BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';

const features = [
  {
    icon: SlidersHorizontal,
    title: "Filter by what matters",
    desc: "Narrow results by subject, grade level, curriculum (CBSE, IGCSE, IB), session type, price range, and language. You're always in control.",
    gradient: "from-[hsl(237,52%,40%)] to-[hsl(237,52%,31%)]",
  },
  {
    icon: UserCheck,
    title: "Explore detailed profiles",
    desc: "Every teacher profile shows their qualifications, teaching experience, subjects offered, session formats, and pricing — all in one place.",
    gradient: "from-[hsl(39,96%,48%)] to-[hsl(30,90%,45%)]",
  },
  {
    icon: Star,
    title: "Read verified reviews",
    desc: "Only students who have completed a session can leave a review. No paid placements, no fake ratings — just real feedback from real learners.",
    gradient: "from-[hsl(260,60%,55%)] to-[hsl(280,55%,50%)]",
  },
  {
    icon: BadgeCheck,
    title: "Trust the Verified badge",
    desc: "Every teacher with a Verified badge has been reviewed by our team — identity confirmed, qualifications checked, and profile approved.",
    gradient: "from-[hsl(152,60%,42%)] to-[hsl(152,55%,35%)]",
  },
];

export default function HowItWorksMatchingPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const title = 'Find the Right Teacher | BookMyCoaching';
  const description = 'Search and filter verified teachers by subject, level, price, and curriculum. Read real reviews from students who have completed sessions.';
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      <main>

        {/* ── Hero ── */}
        <section className="relative pt-28 pb-20 sm:pt-44 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(237,52%,92%)]/80 via-[hsl(237,45%,90%)]/60 to-[hsl(39,96%,93%)]/50" />
          <svg className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="match-grid" x="0" y="0" width="44" height="44" patternUnits="userSpaceOnUse">
                <path d="M 44 0 L 0 0 0 44" fill="none" stroke="hsl(237,52%,31%)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#match-grid)" />
          </svg>
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-white/40 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-[10%] w-[400px] h-[300px] bg-[hsl(39,96%,72%)]/15 rounded-full blur-[100px] pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 border border-[hsl(237,52%,31%)]/20 shadow-sm mb-6">
                <Sparkles className="w-3.5 h-3.5 text-[hsl(39,96%,48%)]" />
                <span className="text-xs font-semibold text-[#262A7A] tracking-wide">Step 2 · For Students</span>
              </div>

              <h1 className="font-sans text-4xl sm:text-5xl lg:text-[3.25rem] font-extrabold text-[#262A7A] leading-[1.1] tracking-tight mb-5">
                Find the teacher<br />
                <span className="relative inline-block text-[hsl(237,52%,31%)]">
                  who fits you best.
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 320 12" fill="none" aria-hidden="true">
                    <path d="M4 9 C 80 3, 240 3, 316 9" stroke="hsl(39,96%,48%)" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                </span>
              </h1>

              <p className="text-[#5A5E80] text-lg sm:text-xl leading-relaxed max-w-2xl mb-10">
                Browse our catalog of verified teachers. Filter by subject, price, and availability — then read real reviews from students before you decide.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="rounded-full px-8 gap-2 bg-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,25%)] text-white shadow-xl shadow-[hsl(237,52%,31%)]/25 hover:-translate-y-0.5 transition-all"
                  onClick={() => navigate('/search')}
                >
                  Browse Teachers
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-full px-8" asChild>
                  <Link to="/how-it-works/first-session">Your first session →</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Search features ── */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-14">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,50%)] mb-3 px-3 py-1 rounded-full bg-[hsl(237,52%,31%)]/10">
                How search works
              </span>
              <h2 className="font-sans text-3xl sm:text-4xl text-[#262A7A] mb-3">Everything you need to choose confidently</h2>
              <p className="text-[#5A5E80] text-sm max-w-md mx-auto">No guesswork. Browse, compare, and read reviews before you book a thing.</p>
            </div>

            <div className="relative">
              <div className="hidden md:block absolute left-[2.25rem] top-14 bottom-14 w-px bg-gradient-to-b from-[hsl(237,52%,40%)]/40 via-[hsl(237,52%,40%)]/20 to-transparent" />

              <div className="flex flex-col gap-6">
                {features.map((f, i) => (
                  <div key={i} className="flex gap-5 items-start group">
                    <div className="flex-shrink-0 z-10">
                      <div className={`w-[4.5rem] h-[4.5rem] rounded-2xl bg-gradient-to-br ${f.gradient} flex items-center justify-center shadow-lg`}>
                        <f.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 rounded-2xl border border-[#DCDDEE] bg-[#F7F7FC]/60 p-6 hover:shadow-md hover:-translate-y-0.5 hover:border-[#DCDDEE] transition-all duration-200">
                      <h3 className="font-semibold text-[#262A7A] text-base mb-1.5 group-hover:text-[hsl(237,52%,31%)] transition-colors">{f.title}</h3>
                      <p className="text-sm text-[#5A5E80] leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(237,52%,25%)] via-[hsl(237,52%,31%)] to-[hsl(237,52%,20%)]" />
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-white/5 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-[hsl(39,96%,48%)]/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="max-w-xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium mb-8 backdrop-blur-md">
                <Search className="w-3.5 h-3.5 text-[hsl(39,96%,58%)]" />
                No account needed to browse
              </div>
              <h2 className="font-sans text-4xl sm:text-5xl text-white mb-5 leading-tight">
                Start your search now
              </h2>
              <p className="text-indigo-200 text-lg mb-10 leading-relaxed">
                Explore verified teachers across hundreds of subjects, levels, and curricula.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  className="rounded-full px-10 py-6 bg-white text-[hsl(237,52%,31%)] hover:bg-white/90 shadow-xl shadow-black/20 hover:-translate-y-0.5 transition-all font-semibold"
                  onClick={() => navigate('/search')}
                >
                  Browse Teachers
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-full px-10 py-6 border-white/30 text-white hover:bg-white/10" asChild>
                  <Link to="/how-it-works">Learn more first</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
